import { Controller } from "@hotwired/stimulus"

// QuizPets roaming overlay.
//
// House pattern (see streak_chest_controller): the rAF loop writes CSS custom
// properties and NOTHING else; @keyframes and transform do the drawing.
//
// The frame index is driven from the loop rather than CSS steps() so gait speed
// is tied to walking speed — a sleepy pet steps slowly. Idle and sleep loops are
// speed-independent and stay in CSS.
//
// Ad safety is geometric: Ezoic slot rects are measured and treated as walls.
// Stacking order (z-index 900) is only the backstop.

const ROW = { down: 0, up: 1, side: 2, idle: 3, sleep: 4, react: 5 }
const FRAMES = 4
const STEP_PX = 14            // pixels of travel per sprite frame
const REACT_MS = 900
const REACT_THROTTLE_MS = 1500
const REMEASURE_MS = 4000
const AD_MARGIN = 12

// Row is chosen by the DOMINANT axis of travel, but not at a plain 45 degrees.
// A uniformly random target makes vertical win ~half the time, and half of that
// is the back view -- the one row with no face. Biasing against "away" keeps the
// pet facing the viewer more without ever looking like it is sliding sideways.
//   1.0 = true 45-degree split
//   >1  = motion must be MORE vertical than that before the vertical row wins
const VERT_BIAS_TOWARD = 1.00   // walking toward the viewer: face is visible, no bias
const VERT_BIAS_AWAY   = 1.70   // walking away: prefer the side view unless clearly vertical

const SPEED = { happy: 46, content: 38, sleepy: 24, asleep: 16 }        // px/s
const DWELL = {                                                         // ms
  happy:   [600, 2200],
  content: [1200, 3800],
  sleepy:  [3000, 8000],
  asleep:  [8000, 20000]
}
const SLEEP_CHANCE = { happy: 0.05, content: 0.15, sleepy: 0.45, asleep: 0.85 }

const AD_SELECTORS = [
  "[id^='ezoic-pub-ad']",
  "[id^='ezoic-pub-video']",
  ".ezoic-ad",
  "ins.adsbygoogle",
  "[data-ad-slot]",
  ".ad-slot",
  "nav",
  ".ad-free-side-tab"
].join(",")

export default class extends Controller {
  static targets = ["sprite", "particles", "dismiss"]
  static values  = { mood: String, stage: Number, overlayUrl: String }

  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.size    = this.element.offsetWidth || 72

    this.zones = []
    this.measure()

    this.onResize = this.measure.bind(this)
    this.onScroll = this.throttled(this.measure.bind(this), 250)
    this.onGrew   = this.handleGrew.bind(this)
    window.addEventListener("resize", this.onResize)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("pet:grew", this.onGrew)
    // Ezoic injects late, so one measurement at connect is not enough.
    this.remeasureTimer = setInterval(() => this.measure(), REMEASURE_MS)

    this.frameAccum = 0
    this.flip = 1
    this.row = ROW.idle
    this.target = null
    this.reactUntil = 0
    this.lastReact = 0
    this.prev = 0
    this.lastSave = 0

    // data-turbo-permanent already carries this node across Turbo visits, so the
    // pet keeps walking mid-stride. Restore only covers FULL page loads (hard
    // refresh, first paint, arriving from a `turbo-visit-control: reload` page),
    // where the node really is rebuilt and would otherwise snap to the corner.
    if (!this.restore()) {
      this.x = this.bounds.left + 24
      this.y = this.bounds.bottom - this.size - 24
    }

    this.write()

    this.onPersist = () => this.persist()
    window.addEventListener("pagehide", this.onPersist)
    document.addEventListener("visibilitychange", this.onPersist)

    if (this.reduced) {
      this.setState(this.restoredState || "idle")
      return
    }

    this.element.addEventListener("click", this.react.bind(this))
    this.dwellUntil = performance.now() + 400
    this.setState(this.restoredState || "idle")
    this.tick = this.tick.bind(this)
    this.raf = requestAnimationFrame(this.tick)
  }

  // --- hiding -------------------------------------------------------------
  //
  // ONE mechanism, deliberately. An earlier version had a session-only hide here
  // as well as the durable setting, and the two could disagree: hiding with the
  // X wrote sessionStorage, so turning the setting back on re-rendered the
  // overlay and connect() immediately hid it again -- "turning it on does
  // nothing". The X now writes the same preference the settings toggle does.

  hide(event) {
    if (event) {
      event.stopPropagation()   // don't also trigger react()
      event.preventDefault()
    }

    // Optimistic: hide now, tell the server after. If the request fails the pet
    // simply returns on the next page load rather than silently staying gone.
    this.element.classList.add("pet-hidden")
    cancelAnimationFrame(this.raf)
    clearInterval(this.remeasureTimer)

    if (!this.overlayUrlValue) return
    fetch(this.overlayUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        // REQUIRED: ApplicationPortalController null-sessions token-less
        // requests, which would fail authenticate_user!.
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: "enabled=0"
    }).catch(() => { /* stays hidden for this page view; returns on reload */ })
  }

  // Mobile has no hover, so a tap reveals the dismiss button for a few seconds.
  poke() {
    this.element.classList.add("pet-poked")
    clearTimeout(this.pokeTimer)
    this.pokeTimer = setTimeout(() => this.element.classList.remove("pet-poked"), 3200)
  }

  // --- persistence --------------------------------------------------------
  //
  // sessionStorage, not localStorage: position is per-tab. Two tabs are two
  // viewports, and a pet teleporting to wherever it stood in another window
  // reads as a glitch, not as life. It also clears itself when the tab closes,
  // so a genuinely new visit starts fresh.

  // Deliberately NOT keyed by stage or pet id: levelling up or switching the
  // active pet must not teleport the pet back to the corner. The viewport is
  // what the position belongs to.
  storageKey() {
    return "quizpets:pos"
  }

  persist() {
    try {
      sessionStorage.setItem(this.storageKey(), JSON.stringify({
        x: Math.round(this.x), y: Math.round(this.y),
        row: this.row, flip: this.flip,
        state: this.state === "sleep" ? "sleep" : "idle",
        vw: window.innerWidth, vh: window.innerHeight
      }))
    } catch (e) {
      /* private mode / blocked storage: position just resets, nothing breaks */
    }
  }

  restore() {
    let saved
    try {
      saved = JSON.parse(sessionStorage.getItem(this.storageKey()) || "null")
    } catch (e) {
      return false
    }
    if (!saved || !Number.isFinite(saved.x) || !Number.isFinite(saved.y)) return false

    // The viewport may have changed since (rotation, resize, a different
    // window). Scale proportionally rather than clamping, so the pet keeps its
    // relative place on the page instead of piling up against an edge.
    const sx = saved.vw ? window.innerWidth / saved.vw : 1
    const sy = saved.vh ? window.innerHeight / saved.vh : 1
    let x = saved.x * sx
    let y = saved.y * sy

    x = Math.min(Math.max(x, this.bounds.left), this.bounds.right - this.size)
    y = Math.min(Math.max(y, this.bounds.top), this.bounds.bottom - this.size)

    // Ad slots move between pages. If the restored spot is now inside one, fall
    // back rather than materialising on top of an ad.
    if (this.blocked(x, y)) return false

    this.x = x
    this.y = y
    this.row = Number.isFinite(saved.row) ? saved.row : ROW.idle
    this.flip = saved.flip === -1 ? -1 : 1
    this.restoredState = saved.state === "sleep" ? "sleep" : "idle"
    return true
  }

  disconnect() {
    this.persist()
    cancelAnimationFrame(this.raf)
    clearInterval(this.remeasureTimer)
    window.removeEventListener("pagehide", this.onPersist)
    document.removeEventListener("visibilitychange", this.onPersist)
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("pet:grew", this.onGrew)
  }

  // --- geometry -----------------------------------------------------------

  measure() {
    const pad = 8
    this.bounds = {
      left:   pad,
      top:    pad,
      right:  window.innerWidth  - pad,
      bottom: window.innerHeight - pad
    }

    this.zones = []
    document.querySelectorAll(AD_SELECTORS).forEach((el) => {
      const r = el.getBoundingClientRect()
      if (r.width < 2 || r.height < 2) return
      this.zones.push({
        left:   r.left   - AD_MARGIN,
        top:    r.top    - AD_MARGIN,
        right:  r.right  + AD_MARGIN,
        bottom: r.bottom + AD_MARGIN
      })
    })
  }

  blocked(x, y) {
    const r = { left: x, top: y, right: x + this.size, bottom: y + this.size }
    if (r.left < this.bounds.left || r.top < this.bounds.top ||
        r.right > this.bounds.right || r.bottom > this.bounds.bottom) return true

    return this.zones.some((z) =>
      r.left < z.right && r.right > z.left && r.top < z.bottom && r.bottom > z.top
    )
  }

  pickTarget() {
    for (let i = 0; i < 24; i++) {
      const x = this.bounds.left + Math.random() * (this.bounds.right - this.bounds.left - this.size)
      const y = this.bounds.top  + Math.random() * (this.bounds.bottom - this.bounds.top - this.size)
      if (!this.blocked(x, y)) return { x, y }
    }
    return null   // nowhere legal right now; stay put and idle
  }

  // --- loop ---------------------------------------------------------------

  tick(now) {
    this.raf = requestAnimationFrame(this.tick)
    if (document.hidden) { this.prev = now; return }

    const dt = this.prev ? Math.min((now - this.prev) / 1000, 0.05) : 0
    this.prev = now

    // The react pose holds for REACT_MS, then always falls back to idle.
    // (setState("walk") does not own a row, so returning to "walk" here would
    // strand the sprite on the react row.)
    if (now < this.reactUntil) { this.write(); return }
    if (this.row === ROW.react) { this.dwellUntil = now + 300; this.setState("idle") }

    if (this.state !== "walk") {
      if (now < this.dwellUntil) return
      this.target = this.pickTarget()
      if (!this.target) { this.dwellUntil = now + 1200; return }
      this.setState("walk")
      return
    }

    const mood  = this.moodValue || "content"
    const speed = SPEED[mood] ?? SPEED.content
    const dx = this.target.x - this.x
    const dy = this.target.y - this.y
    const dist = Math.hypot(dx, dy)

    if (dist < 2) { this.arrive(now, mood); return }

    const stepX = (dx / dist) * speed * dt
    const stepY = (dy / dist) * speed * dt

    if (this.blocked(this.x + stepX, this.y + stepY)) { this.arrive(now, mood); return }

    this.x += stepX
    this.y += stepY

    const bias = dy > 0 ? VERT_BIAS_TOWARD : VERT_BIAS_AWAY
    if (Math.abs(dy) > Math.abs(dx) * bias) {
      this.row  = dy > 0 ? ROW.down : ROW.up
      this.flip = 1
    } else {
      this.row  = ROW.side
      this.flip = dx < 0 ? -1 : 1
    }

    this.frameAccum += (speed * dt) / STEP_PX
    this.write()

    // Throttled: one write a second, never per frame.
    if (now - this.lastSave > 1000) {
      this.lastSave = now
      this.persist()
    }
  }

  arrive(now, mood) {
    const [lo, hi] = DWELL[mood] ?? DWELL.content
    this.dwellUntil = now + lo + Math.random() * (hi - lo)
    this.setState(Math.random() < (SLEEP_CHANCE[mood] ?? 0.15) ? "sleep" : "idle")
    this.write()
    this.persist()
  }

  setState(state) {
    this.state = state
    this.element.dataset.petState = state
    if (state === "idle")  this.row = ROW.idle
    if (state === "sleep") this.row = ROW.sleep
    this.write()
  }

  write() {
    const s = this.element.style
    s.setProperty("--pet-x", `${this.x.toFixed(1)}px`)
    s.setProperty("--pet-y", `${this.y.toFixed(1)}px`)
    s.setProperty("--pet-row", this.row)
    s.setProperty("--pet-flip", this.flip)
    s.setProperty("--pet-frame", Math.floor(this.frameAccum) % FRAMES)
  }

  // --- interaction --------------------------------------------------------

  react() {
    this.poke()
    const now = performance.now()
    if (now - this.lastReact < REACT_THROTTLE_MS) return
    this.lastReact = now

    this.row = ROW.react
    this.reactUntil = now + REACT_MS
    this.element.dataset.petState = "react"
    this.spawnParticles()
    if (navigator.vibrate) navigator.vibrate(15)
    this.write()
  }

  spawnParticles() {
    if (this.reduced) return

    for (let i = 0; i < 8; i++) {
      const p = document.createElement("span")
      p.className = "pet-particle"
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4
      const dist  = 30 + Math.random() * 45
      p.style.setProperty("--dx", `${Math.cos(angle) * dist}px`)
      p.style.setProperty("--dy", `${Math.sin(angle) * dist}px`)
      p.style.setProperty("--sz", `${7 + Math.random() * 6}px`)
      p.style.setProperty("--delay", `${Math.random() * 200}ms`)
      p.addEventListener("animationend", () => p.remove())
      this.particlesTarget.appendChild(p)
    }
  }

  // The overlay is data-turbo-permanent, so it is NOT re-rendered on level-up.
  // The feed controller tells us the new sheet instead.
  handleGrew(event) {
    const { stage, sheetUrl } = event.detail || {}
    if (!sheetUrl) return
    this.stageValue = stage
    this.element.style.setProperty("--pet-sheet", `url('${sheetUrl}')`)
    this.moodValue = "happy"
    this.react()
  }

  throttled(fn, ms) {
    let last = 0
    return (...args) => {
      const now = Date.now()
      if (now - last < ms) return
      last = now
      fn(...args)
    }
  }
}
